import ReactECharts from 'echarts-for-react';

/**
 * RaisyStackChart — the four-level Entity → Process → Bot → Kernel stack
 * that every Raisy company is built from (blog01).
 *
 * Chart-type-first: this is HIERARCHICAL data (a company owns processes,
 * processes own bots, bots load kernels) → the best-fit engine for hierarchy
 * is a sunburst. Built with ECharts' native sunburst, not a hacked bar chart.
 *
 * Brand colors from theme.ts; opaque white background (RULES_BLOG_POST opaque-bg rule).
 */

const BRAND = {
  tealDark: '#087B8B',
  teal: '#0AB3BF',
  tealLight: '#4DD4E0',
  info: '#3B82F6',
  slate: '#334155',
  muted: '#64748B',
  surface: '#FFFFFF',
};

interface StackNode {
  name: string;
  value?: number;
  itemStyle?: { color: string };
  children?: StackNode[];
}

const stackData: StackNode[] = [
  {
    name: 'Entity',
    itemStyle: { color: BRAND.tealDark },
    children: [
      {
        name: 'Process',
        itemStyle: { color: BRAND.teal },
        children: [
          {
            name: 'Bot',
            itemStyle: { color: BRAND.tealLight },
            children: [
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
            ],
          },
          {
            name: 'Bot',
            itemStyle: { color: BRAND.tealLight },
            children: [
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
            ],
          },
        ],
      },
      {
        name: 'Process',
        itemStyle: { color: BRAND.teal },
        children: [
          {
            name: 'Bot',
            itemStyle: { color: BRAND.tealLight },
            children: [
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
            ],
          },
        ],
      },
    ],
  },
];

/** ONE shared level-name + description renderer used by label and tooltip. */
const levelMeta: Record<string, string> = {
  Entity: 'Who acts — companies, products, people',
  Process: 'What they do repeatedly — workflows in a domain',
  Bot: 'Who executes each step — bots or humans',
  Kernel: 'What the actor knows — rules, context, schema',
};

const labelOf = (name: string) => name;

const tooltipOf = (name: string) => `${name}<br/><span style="color:#64748B">${levelMeta[name] ?? 'Raisy building block'}</span>`;

export const RaisyStackChart = ({ height = 380 }: { height?: number }) => {
  const option = {
    backgroundColor: BRAND.surface,
    tooltip: {
      trigger: 'item',
      backgroundColor: '#1E293B',
      borderColor: BRAND.slate,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (p: { name: string }) => tooltipOf(p.name),
    },
    series: [
      {
        type: 'sunburst',
        data: stackData,
        radius: [0, '92%'],
        center: ['50%', '52%'],
        sort: undefined,
        emphasis: { focus: 'ancestor' },
        label: {
          rotate: 0,
          color: '#FFFFFF',
          fontSize: 13,
          fontWeight: 700,
          formatter: (p: { name: string }) => labelOf(p.name),
        },
        itemStyle: { borderColor: BRAND.surface, borderWidth: 2 },
        levels: [
          {},
          { r0: '0%', r: '34%', label: { fontSize: 16, color: '#FFFFFF', fontWeight: 800 } },
          { r0: '34%', r: '62%', label: { fontSize: 13, color: '#FFFFFF', fontWeight: 700 } },
          { r0: '62%', r: '82%', label: { fontSize: 12, color: '#083344', fontWeight: 700 } },
          { r0: '82%', r: '100%', label: { fontSize: 9, color: '#FFFFFF', fontWeight: 600 } },
        ],
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 2. The Raisy stack, top-down: an Entity owns Processes, each Process is executed by Bots, and each Bot
        loads Kernels — the rules it knows. Click a segment to zoom; hover for the level definition.
      </p>
    </div>
  );
};
